import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Row, Col, Card, CardBody, Spinner } from "reactstrap";
import BaseInput from "../../Components/Common/BaseInput";
import BaseButton from "../../Components/Common/BaseButton";
import { toast } from "react-toastify";
import { Link, useNavigate, useParams } from "react-router-dom";
import Icon from "@mdi/react";
import * as Yup from "yup";
import { mdiTrashCan, mdiPlus, mdiArrowLeft } from "@mdi/js";
import {
  REQUEST_ADD_SERVICE_COMMON,
  REQUEST_FILE_UPLOAD_COMMON,
  REQUEST_UPDATE_SERVICE_COMMON,
  REQUEST_VIEW_SERVICE_COMMON,
} from "../../API/Services/common";
import {
  ADD_SERVICE,
  ADD_SERVICE_TITLE,
  APPROACH,
  ATC,
  BACK,
  DESCRIPTION_LABEL,
  DETAILS_LABEL,
  EDIT_SERVICE,
  IMAGE_ONE_MB_TOAST,
  ONLY_IMAGES_ALLOWED_TOAST,
  OVERVIEW_IMG,
  RS1_IMG,
  RS2_IMG,
  SERVICE_APPROACH,
  SERVICE_ATC,
  SERVICE_BENEFITS,
  SERVICE_CONSULTING,
  SERVICE_DESC,
  SERVICE_IMG,
  SERVICENAME_HEADER,
  SUB_SERVICE_LABEL,
  SUBMIT_LABEL,
  SUBMITTING_LABEL,
  TITLE_HEADER,
  UPDATE_LABEL,
  UPDATING_LABEL,
} from "Components/constants/commonNames";
import { checkStatusCodeSuccess } from "Components/constants/enums";
import {
  InputPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import { BaseImageURL } from "API/Services/services";

interface ServiceDetail {
  services_details_point: string;
  services_details_description?: string;
}

interface SubService {
  sub_service_title: string;
  sub_service_description: string;
}

interface FormValues {
  service_name: string;
  service_description: string;
  overview_image: string;
  service_image: string;
  right_sidebar_image_1: string;
  right_sidebar_image_2: string;
  subService: SubService[];
  serviceDetailsApproach: ServiceDetail[];
  serviceDetailsATC: ServiceDetail[];
  serviceDetailsBenefits: ServiceDetail[];
  serviceDetailsConsulting: ServiceDetail[];
}

const ServicesPage: React.FC = () => {
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const [viewServiceData, setviewServiceData] = useState<any>();
  const { id } = useParams<{ id: string }>();
  const [fullScreenLoader, setFullScreenLoader] = useState(false);
  const serviceId = id ? Number(id) : undefined;

  const validationSchema = Yup.object().shape({
    service_name: Yup.string().required(
      validationMessages.required(SERVICENAME_HEADER)
    ),
    service_description: Yup.string().required(
      validationMessages.required(SERVICE_DESC)
    ),

    overview_image: Yup.mixed().required(
      validationMessages.required(OVERVIEW_IMG)
    ),

    service_image: Yup.mixed().required(
      validationMessages.required(SERVICE_IMG)
    ),

    right_sidebar_image_1: Yup.mixed().required(
      validationMessages.required(RS1_IMG)
    ),

    right_sidebar_image_2: Yup.mixed().required(
      validationMessages.required(RS2_IMG)
    ),
  });

  const formik = useFormik<FormValues>({
    validationSchema,
    enableReinitialize: true,
    initialValues: {
      service_name: id ? String(viewServiceData?.service_name) : "",
      service_description: id
        ? String(viewServiceData?.service_description)
        : "",
      overview_image: id
        ? String(viewServiceData?.serviceImages?.[0]?.overview_image)
        : "",
      service_image: id
        ? String(viewServiceData?.serviceImages?.[0]?.service_image)
        : "",
      right_sidebar_image_1: id
        ? String(viewServiceData?.serviceImages?.[0]?.right_sidebar_image_1)
        : "",
      right_sidebar_image_2: id
        ? String(viewServiceData?.serviceImages?.[0]?.right_sidebar_image_2)
        : "",

      subService: id
        ? viewServiceData?.subService?.map((sub: any) => ({
            sub_service_title: sub.sub_service_title,
            sub_service_description: sub.sub_service_description,
          })) || [{ sub_service_title: "", sub_service_description: "" }]
        : [{ sub_service_title: "", sub_service_description: "" }],

      serviceDetailsApproach: id
        ? viewServiceData?.Approach?.map((item: any) => ({
            services_details_point: item.services_details_point,
          })) || [{ services_details_point: "" }]
        : [{ services_details_point: "" }],

      serviceDetailsATC: id
        ? viewServiceData?.ATC?.map((item: any) => ({
            services_details_point: item.services_details_point,
          })) || [{ services_details_point: "" }]
        : [{ services_details_point: "" }],

      serviceDetailsBenefits: id
        ? viewServiceData?.Benefit?.map((item: any) => ({
            services_details_point: item.services_details_point,
          })) || [{ services_details_point: "" }]
        : [{ services_details_point: "" }],

      serviceDetailsConsulting: id
        ? viewServiceData?.Consulting?.map((detail: any) => ({
            services_details_point: detail.services_details_point,
            services_details_description: detail.services_details_description,
          })) || [
            { services_details_point: "", services_details_description: "" },
          ]
        : [{ services_details_point: "", services_details_description: "" }],
    },
    onSubmit: async (values) => {
      setLoader(true);
      const images = [
        {
          overview_image: values?.overview_image,
          service_image: values.service_image,
          right_sidebar_image_1: values.right_sidebar_image_1,
          right_sidebar_image_2: values.right_sidebar_image_2,
        },
      ];

      const payload = {
        service_name: values.service_name,
        service_description: values.service_description,
        serviceImageDetail: images,
        subService: values.subService.map((sub) => ({
          sub_service_title: sub.sub_service_title,
          sub_service_description: sub.sub_service_description,
        })),
        serviceDetailsApproach: values.serviceDetailsApproach.map((detail) => ({
          services_details_point: detail.services_details_point,
        })),
        serviceDetailsATC: values.serviceDetailsATC.map((detail) => ({
          services_details_point: detail.services_details_point,
        })),
        serviceDetailsBenefits: values.serviceDetailsBenefits.map((detail) => ({
          services_details_point: detail.services_details_point,
        })),
        serviceDetailsConsulting: values.serviceDetailsConsulting.map(
          (detail) => ({
            services_details_point: detail.services_details_point,
            services_details_description: detail.services_details_description,
          })
        ),
      };

      try {
        const resp = serviceId
          ? await REQUEST_UPDATE_SERVICE_COMMON(serviceId, payload)
          : await REQUEST_ADD_SERVICE_COMMON(payload);

        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success(resp.message);
          navigate("/services");
        } else {
          toast.error(resp.message);
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || error.message);
      } finally {
        setLoader(false);
      }
    },
  });

  const addServiceDetail = (field: keyof FormValues) => {
    const details = formik.values[field];
    if (Array.isArray(details)) {
      const newDetail =
        field === "serviceDetailsConsulting"
          ? { services_details_point: "", services_details_description: "" }
          : { services_details_point: "" };
      formik.setFieldValue(field, [...details, newDetail]);
    }
  };

  const removeServiceDetail = (field: keyof FormValues, index: number) => {
    const details = formik.values[field];
    if (Array.isArray(details)) {
      const updatedDetails = details.filter((_, i) => i !== index);
      formik.setFieldValue(field, updatedDetails);
    }
  };

  const handleFileChange = async (event: any, imageType: string) => {
    const fileInput = event.target;
    const file = fileInput?.files[0];
    const maxSizeInBytes = 1024 * 1024;

    if (file && file.size > maxSizeInBytes) {
      toast.error(IMAGE_ONE_MB_TOAST);
      fileInput.value = "";
      return;
    }

    const allowedImageTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (file && !allowedImageTypes.includes(file.type)) {
      toast.error(ONLY_IMAGES_ALLOWED_TOAST);
      fileInput.value = "";
      return;
    }

    if (file) {
      setFullScreenLoader(true);

      const formData = new FormData();
      formData.append("files", file);

      try {
        const resp: any = await REQUEST_FILE_UPLOAD_COMMON(formData);
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          formik.setFieldValue(imageType, String(resp?.data[0]?.file));
        } else {
          toast.error(resp.message);
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || error.message);
      } finally {
        setFullScreenLoader(false);
      }
    }
  };

  const handleViewService = async (id: any) => {
    setFullScreenLoader(true);

    try {
      const resp: any = await REQUEST_VIEW_SERVICE_COMMON(id);
      if (checkStatusCodeSuccess(resp?.statusCode)) {
        setviewServiceData(resp?.data);
        toast.success(resp.message);
      } else {
        toast.error(resp.message);
      }
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setFullScreenLoader(false);
    }
  };

  useEffect(() => {
    if (serviceId) {
      handleViewService(serviceId);
    }
  }, [serviceId]);

  document.title = ADD_SERVICE_TITLE;

  return (
    <div className="top-div">
      <div className="full-width-container">
        <Row className="align-items-center mt-2 mb-1">
          <Col md={6}>
            <span className="fs-4 px-2">{id ? EDIT_SERVICE : ADD_SERVICE}</span>
          </Col>
          <Col md={6}>
            <BaseButton
              type="button"
              onClick={() => navigate("/services")}
              text={
                <>
                  <Icon path={mdiArrowLeft} size={0.7} className="me-2" />
                  {BACK}
                </>
              }
              color="info"
              className="mt-0 w-auto float-end"
            />
          </Col>
        </Row>
        <Card>
          <CardBody>
            <form
              onSubmit={formik.handleSubmit}
              className="d-flex w-100 flex-column"
            >
              <div className="border-bottom">
                <Row className="mb-0">
                  <Col xs={12} sm={6} md={4} className="mb-3">
                    <BaseInput
                      required={true}
                      label={SERVICENAME_HEADER}
                      name="service_name"
                      type="text"
                      placeholder={InputPlaceHolder(SERVICENAME_HEADER)}
                      value={formik.values.service_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.errors.service_name}
                      touched={formik.touched.service_name}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={4} className="mb-3">
                    <BaseInput
                      label={SERVICE_DESC}
                      required={true}
                      name="service_description"
                      type="textarea"
                      placeholder={InputPlaceHolder(SERVICE_DESC)}
                      value={formik.values.service_description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.errors.service_description}
                      touched={formik.touched.service_description}
                      rows={3}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={4} className="mb-3">
                    <BaseInput
                      accept="image/*"
                      label={OVERVIEW_IMG}
                      required={true}
                      name="overview_image"
                      tooltip={true}
                      tooltipIcon="help-circle-outline"
                      tooltipIconColor="info"
                      tooltipText="Only JPEG/PNG Allowed"
                      type="file"
                      onChange={(event) => {
                        handleFileChange(event, "overview_image");
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.errors.overview_image}
                      touched={formik.touched.overview_image}
                    />
                    {formik.values.overview_image && (
                      <span className="fw-normal mb-2 d-flex">
                        <Link
                          target="_blank"
                          className="text-link font-weight-bold"
                          to={`${BaseImageURL}${formik.values.overview_image}`}
                        >
                          View File
                        </Link>
                      </span>
                    )}
                  </Col>
                  <Col xs={12} sm={6} md={4} className="mb-3">
                    <BaseInput
                      accept="image/*"
                      label={SERVICE_IMG}
                      required={true}
                      name="service_image"
                      tooltip={true}
                      tooltipIcon="help-circle-outline"
                      tooltipIconColor="info"
                      tooltipText="Only JPEG/PNG Allowed"
                      type="file"
                      onChange={(event) => {
                        handleFileChange(event, "service_image");
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.errors.service_image}
                      touched={formik.touched.service_image}
                    />
                    {formik.values.service_image && (
                      <span className="fw-normal mb-2 d-flex">
                        <Link
                          target="_blank"
                          className="text-link font-weight-bold"
                          to={`${BaseImageURL}${formik.values.service_image}`}
                        >
                          View File
                        </Link>
                      </span>
                    )}
                  </Col>
                  <Col xs={12} sm={6} md={4} className="mb-3">
                    <BaseInput
                      accept="image/*"
                      label={RS1_IMG}
                      name="right_sidebar_image_1"
                      required={true}
                      tooltip={true}
                      tooltipIcon="help-circle-outline"
                      tooltipIconColor="info"
                      tooltipText="Only JPEG/PNG Allowed"
                      type="file"
                      onChange={(event) => {
                        handleFileChange(event, "right_sidebar_image_1");
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.errors.right_sidebar_image_1}
                      touched={formik.touched.right_sidebar_image_1}
                    />
                    {formik.values.right_sidebar_image_1 && (
                      <span className="fw-normal mb-2 d-flex">
                        <Link
                          target="_blank"
                          className="text-link font-weight-bold"
                          to={`${BaseImageURL}${formik.values.right_sidebar_image_1}`}
                        >
                          View File
                        </Link>
                      </span>
                    )}
                  </Col>
                  <Col xs={12} sm={6} md={4} className="mb-3">
                    <BaseInput
                      label={RS2_IMG}
                      accept="image/*"
                      tooltip={true}
                      tooltipIcon="help-circle-outline"
                      tooltipIconColor="info"
                      tooltipText="Only JPEG/PNG Allowed"
                      name="right_sidebar_image_2"
                      required={true}
                      type="file"
                      onChange={(event) => {
                        handleFileChange(event, "right_sidebar_image_2");
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.errors.right_sidebar_image_2}
                      touched={formik.touched.right_sidebar_image_2}
                    />
                    {formik.values.right_sidebar_image_2 && (
                      <span className="fw-normal mb-2 d-flex">
                        <Link
                          target="_blank"
                          className="text-link font-weight-bold"
                          to={`${BaseImageURL}${formik.values.right_sidebar_image_2}`}
                        >
                          View File
                        </Link>
                      </span>
                    )}
                  </Col>
                </Row>
              </div>

              <div className="border-bottom">
                <Row className="align-items-center mb-0">
                  <Col xs={8} sm={9} md={7}>
                    <span className="fs-4">{SUB_SERVICE_LABEL}</span>
                  </Col>
                  <Col
                    xs={4}
                    sm={3}
                    md={1}
                    className="d-flex justify-content-end"
                  >
                    <BaseButton
                      type="button"
                      onClick={() => addServiceDetail("subService")}
                      text={<Icon path={mdiPlus} className="fs-5" size={0.8} />}
                      color="info"
                      className="btn-sm w-auto px-1"
                      disabled={formik.values.subService.every(
                        (subService) =>
                          !subService.sub_service_title ||
                          !subService.sub_service_description
                      )}
                    />
                  </Col>
                </Row>
                {formik.values.subService.map((subService, index) => (
                  <Row key={index} className="mb-0 align-items-center">
                    <Col xs={12} sm={6} md={4}>
                      <BaseInput
                        className="mb-3"
                        label={`${SUB_SERVICE_LABEL} ${TITLE_HEADER}`}
                        name={`subService[${index}].sub_service_title`}
                        type="text"
                        value={subService.sub_service_title}
                        onChange={formik.handleChange}
                        placeholder={InputPlaceHolder(
                          `${SUB_SERVICE_LABEL} ${TITLE_HEADER}`
                        )}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                      <BaseInput
                        className="mb-3"
                        label={`${SUB_SERVICE_LABEL} ${DESCRIPTION_LABEL}`}
                        name={`subService[${index}].sub_service_description`}
                        type="text"
                        value={subService.sub_service_description}
                        onChange={formik.handleChange}
                        placeholder={InputPlaceHolder(
                          `${SUB_SERVICE_LABEL} ${DESCRIPTION_LABEL}`
                        )}
                      />
                    </Col>
                    {index > 0 && (
                      <Col xs={12} sm={6} md={2}>
                        <BaseButton
                          type="button"
                          onClick={() =>
                            removeServiceDetail("subService", index)
                          }
                          text={<Icon path={mdiTrashCan} size={0.5} />}
                          className="btn-danger w-auto btn-sm mt-3 mb-2"
                        />
                      </Col>
                    )}
                  </Row>
                ))}
              </div>

              <Row className="mt-0">
                <Col
                  xs={12}
                  sm={6}
                  md={4}
                  className="border-right border-bottom"
                >
                  <Row className="align-items-center mb-0 ">
                    <Col xs={8} md={10}>
                      <span className="fs-4">{SERVICE_APPROACH}</span>
                    </Col>
                    <Col xs={4} md={2} className="d-flex justify-content-end">
                      <BaseButton
                        type="button"
                        onClick={() =>
                          addServiceDetail("serviceDetailsApproach")
                        }
                        text={
                          <Icon path={mdiPlus} className="fs-5" size={0.8} />
                        }
                        color="info"
                        className="btn-sm w-auto px-1"
                        disabled={formik.values.serviceDetailsApproach.every(
                          (detail) => !detail.services_details_point
                        )}
                      />
                    </Col>
                  </Row>
                  {formik.values.serviceDetailsApproach.map((detail, index) => (
                    <Row key={index} className="align-items-end mb-0">
                      <div className="d-flex justify-content-end mb-0 ">
                        {index > 0 && (
                          <div>
                            <BaseButton
                              type="button"
                              onClick={() =>
                                removeServiceDetail(
                                  "serviceDetailsApproach",
                                  index
                                )
                              }
                              text={<Icon path={mdiTrashCan} size={0.5} />}
                              className="btn-danger btn-sm mt-0"
                            />
                          </div>
                        )}
                      </div>
                      <Col xs={12}>
                        <BaseInput
                          label={APPROACH}
                          className="mb-3"
                          placeholder={InputPlaceHolder(APPROACH)}
                          name={`serviceDetailsApproach[${index}].services_details_point`}
                          type="text"
                          value={detail.services_details_point}
                          onChange={(e) =>
                            formik.setFieldValue(
                              `serviceDetailsApproach[${index}].services_details_point`,
                              e.target.value
                            )
                          }
                        />
                      </Col>
                    </Row>
                  ))}
                </Col>

                <Col
                  xs={12}
                  sm={6}
                  md={4}
                  className="border-right border-bottom"
                >
                  <Row className="align-items-center mb-0">
                    <Col xs={8} md={10}>
                      <span className="fs-4">{SERVICE_ATC}</span>
                    </Col>
                    <Col xs={4} md={2} className="d-flex justify-content-end">
                      <BaseButton
                        type="button"
                        onClick={() => addServiceDetail("serviceDetailsATC")}
                        text={
                          <Icon path={mdiPlus} className="fs-5" size={0.8} />
                        }
                        color="info"
                        className="btn-sm w-auto px-1"
                        disabled={formik.values.serviceDetailsATC.every(
                          (detail) => !detail.services_details_point
                        )}
                      />
                    </Col>
                  </Row>
                  {formik.values.serviceDetailsATC.map((detail, index) => (
                    <Row key={index} className="align-items-end mb-0">
                      <div className="d-flex justify-content-end mb-0 ">
                        {index > 0 && (
                          <div>
                            <BaseButton
                              type="button"
                              onClick={() =>
                                removeServiceDetail("serviceDetailsATC", index)
                              }
                              text={<Icon path={mdiTrashCan} size={0.5} />}
                              className="btn-danger btn-sm mt-0"
                            />
                          </div>
                        )}
                      </div>
                      <Col xs={12}>
                        <BaseInput
                          label={ATC}
                          className="mb-3"
                          placeholder={InputPlaceHolder(ATC)}
                          name={`serviceDetailsATC[${index}].services_details_point`}
                          type="text"
                          value={detail.services_details_point}
                          onChange={(e) =>
                            formik.setFieldValue(
                              `serviceDetailsATC[${index}].services_details_point`,
                              e.target.value
                            )
                          }
                        />
                      </Col>
                    </Row>
                  ))}
                </Col>

                <Col xs={12} sm={6} md={4} className="border-bottom">
                  <Row className="align-items-center mb-0">
                    <Col xs={8} md={10}>
                      <span className="fs-4">{SERVICE_BENEFITS}</span>
                    </Col>
                    <Col xs={4} md={2} className="d-flex justify-content-end">
                      <BaseButton
                        type="button"
                        onClick={() =>
                          addServiceDetail("serviceDetailsBenefits")
                        }
                        text={
                          <Icon path={mdiPlus} className="fs-5" size={0.8} />
                        }
                        color="info"
                        className="btn-sm w-auto px-1"
                        disabled={formik.values.serviceDetailsBenefits.every(
                          (detail) => !detail.services_details_point
                        )}
                      />
                    </Col>
                  </Row>
                  {formik.values.serviceDetailsBenefits.map((detail, index) => (
                    <Row key={index} className="align-items-end mb-0">
                      <div className="d-flex justify-content-end mb-0">
                        {index > 0 && (
                          <div>
                            <BaseButton
                              type="button"
                              onClick={() =>
                                removeServiceDetail(
                                  "serviceDetailsBenefits",
                                  index
                                )
                              }
                              text={<Icon path={mdiTrashCan} size={0.5} />}
                              className="btn-danger btn-sm mt-0 mb-0"
                            />
                          </div>
                        )}
                      </div>
                      <Col xs={12}>
                        <BaseInput
                          label="Benefits"
                          className="mb-3"
                          name={`serviceDetailsBenefits[${index}].services_details_point`}
                          type="text"
                          placeholder={InputPlaceHolder(SERVICE_BENEFITS)}
                          value={detail.services_details_point}
                          onChange={(e) =>
                            formik.setFieldValue(
                              `serviceDetailsBenefits[${index}].services_details_point`,
                              e.target.value
                            )
                          }
                        />
                      </Col>
                    </Row>
                  ))}
                </Col>

                <Col xs={12}>
                  <Row className="align-items-center mb-0 mt-2">
                    <Col xs={8} md={7}>
                      <span className="fs-4">{SERVICE_CONSULTING}</span>
                    </Col>
                    <Col xs={4} md={1} className="d-flex justify-content-end">
                      <BaseButton
                        type="button"
                        onClick={() =>
                          addServiceDetail("serviceDetailsConsulting")
                        }
                        text={
                          <Icon path={mdiPlus} className="fs-5" size={0.8} />
                        }
                        color="info"
                        className="btn-sm w-auto px-1"
                        disabled={formik.values.serviceDetailsConsulting.every(
                          (detail) =>
                            !detail.services_details_point ||
                            !detail.services_details_description
                        )}
                      />
                    </Col>
                  </Row>
                  {formik.values.serviceDetailsConsulting.map(
                    (detail, index) => (
                      <Row key={index} className="align-items-center mb-0">
                        <Col xs={12} sm={6} md={4}>
                          <BaseInput
                            className="mb-3"
                            placeholder={InputPlaceHolder(
                              `${DETAILS_LABEL} ${TITLE_HEADER}`
                            )}
                            label={`${DETAILS_LABEL} ${TITLE_HEADER}`}
                            name={`serviceDetailsConsulting[${index}].services_details_point`}
                            type="text"
                            value={detail.services_details_point}
                            onChange={(e) =>
                              formik.setFieldValue(
                                `serviceDetailsConsulting[${index}].services_details_point`,
                                e.target.value
                              )
                            }
                          />
                        </Col>
                        <Col xs={12} sm={6} md={4}>
                          <BaseInput
                            className="mb-3"
                            placeholder={InputPlaceHolder(
                              `${DETAILS_LABEL} ${DESCRIPTION_LABEL}`
                            )}
                            label={`${DETAILS_LABEL} ${DESCRIPTION_LABEL}`}
                            name={`serviceDetailsConsulting[${index}].services_details_description`}
                            type="text"
                            value={detail.services_details_description}
                            onChange={(e) =>
                              formik.setFieldValue(
                                `serviceDetailsConsulting[${index}].services_details_description`,
                                e.target.value
                              )
                            }
                          />
                        </Col>
                        {index > 0 && (
                          <Col xs={12} md={2}>
                            <BaseButton
                              type="button"
                              onClick={() =>
                                removeServiceDetail(
                                  "serviceDetailsConsulting",
                                  index
                                )
                              }
                              text={<Icon path={mdiTrashCan} size={0.5} />}
                              className="btn-danger btn-sm w-auto mt-2"
                            />
                          </Col>
                        )}
                      </Row>
                    )
                  )}
                </Col>
              </Row>

              <Row className="mt-0 justify-content-end">
                <Col
                  xs={12}
                  md="auto"
                  className="d-flex justify-content-center justify-content-md-end"
                >
                  <BaseButton
                    type="submit"
                    text={
                      id
                        ? loader
                          ? UPDATING_LABEL
                          : UPDATE_LABEL
                        : loader
                        ? SUBMITTING_LABEL
                        : SUBMIT_LABEL
                    }
                    loading={loader}
                    color="success"
                    className="w-auto"
                  />
                </Col>
              </Row>
            </form>
          </CardBody>
        </Card>
        {fullScreenLoader && (
          <div className="full-screen-loader">
            <Spinner />
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesPage;
